import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <div data-testid="contact-info">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#003865]" />
              <span
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#003865]"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {t('contact.overline')}
              </span>
            </div>

            <h2
              data-testid="contact-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#333F48] mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {t('contact.title')}
            </h2>

            <p
              className="text-base leading-relaxed text-[#333F48]/70 mb-12 max-w-md"
              style={{ fontFamily: 'Roboto, sans-serif' }}
            >
              {t('contact.subtitle')}
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#C8C9C7] flex items-center justify-center text-[#003865]">
                  <MapPin size={18} />
                </div>
                <span className="text-sm text-[#333F48]" style={{ fontFamily: 'Roboto, sans-serif' }}>
                  {t('contact.address')}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[#C8C9C7] flex items-center justify-center text-[#003865]">
                  <Globe size={18} />
                </div>
                <span className="text-sm text-[#333F48]" style={{ fontFamily: 'Roboto, sans-serif' }}>
                  {t('contact.website')}
                </span>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div data-testid="contact-form-container">
            {status === 'success' ? (
              <div data-testid="contact-success" className="flex flex-col items-center justify-center h-full text-center py-12">
                <CheckCircle size={48} className="text-[#003865] mb-6" />
                <p className="text-lg font-medium text-[#333F48]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('contact.success')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t('contact.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-input-name"
                      className="w-full bg-transparent border-b border-[#C8C9C7] focus:border-[#003865] outline-none py-3 text-[#333F48] transition-colors"
                      style={{ fontFamily: 'Roboto, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-input-email"
                      className="w-full bg-transparent border-b border-[#C8C9C7] focus:border-[#003865] outline-none py-3 text-[#333F48] transition-colors"
                      style={{ fontFamily: 'Roboto, sans-serif' }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t('contact.company')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      data-testid="contact-input-company"
                      className="w-full bg-transparent border-b border-[#C8C9C7] focus:border-[#003865] outline-none py-3 text-[#333F48] transition-colors"
                      style={{ fontFamily: 'Roboto, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="contact-input-phone"
                      className="w-full bg-transparent border-b border-[#C8C9C7] focus:border-[#003865] outline-none py-3 text-[#333F48] transition-colors"
                      style={{ fontFamily: 'Roboto, sans-serif' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {t('contact.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-b border-[#C8C9C7] focus:border-[#003865] outline-none py-3 text-[#333F48] transition-colors resize-none"
                    style={{ fontFamily: 'Roboto, sans-serif' }}
                  />
                </div>

                {status === 'error' && (
                  <p data-testid="contact-error" className="text-sm text-red-600">{t('contact.error')}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid="contact-submit-button"
                  className="group bg-[#003865] text-white px-8 py-4 font-semibold uppercase tracking-wide text-sm flex items-center gap-3 hover:bg-[#004B87] transition-colors duration-200 disabled:opacity-60"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {status === 'sending' ? t('contact.sending') : t('contact.submit')}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
